import React from 'react';

/**
 * Parses a text string and returns a React element with styled text
 * Supports **text** for bold styling
 * @param text - The text string to parse
 * @returns React element with styled text
 */
export const parseStyledText = (text: string): React.ReactElement => {
  // Split by ** to identify bold sections
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return (
    <span>
      {parts.map((part, index) => {
        // Check if this part is wrapped in ** (bold)
        if (part.startsWith('**') && part.endsWith('**')) {
          const boldText = part.slice(2, -2); // Remove ** from start and end
          return (
            <span key={index} style={{ fontWeight: 'bold' }}>
              {boldText}
            </span>
          );
        }
        // Regular text
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
};

/**
 * Hook to get styled text for components
 * @param text - The text string
 * @returns React element with styled text
 */
export const useStyledText = (text: string): React.ReactElement => {
  return React.useMemo(() => parseStyledText(text), [text]);
};

// Legacy exports for backward compatibility
export const parseStyledPlaceholder = parseStyledText;
export const useStyledPlaceholder = useStyledText;

/**
 * Strips leading whitespace from a search/filter input value.
 * Pasting data copied from table columns often carries a leading space, which
 * breaks exact-match search/filter (NX-4682). Leading-only (not trailing) so
 * users can still type multi-word search terms.
 * @param value - The raw input/search string
 * @returns The value with leading whitespace removed
 */
export const stripLeadingWhitespace = (value: string): string =>
  typeof value === 'string' ? value.replace(/^\s+/, '') : value;
